/**
 * Google OAuth Helper
 * Handles direct Google OAuth flow (without Firebase)
 * Backend exchanges authorization code for tokens
 */

import { logger } from '../../utils/logger';

export interface GoogleOAuthConfig {
  clientId: string;
  redirectUri: string;
  scope?: string;
}

export interface GoogleAuthResult {
  code: string;
  state?: string;
}

/**
 * Google OAuth Helper Class
 * Manages Google OAuth consent flow and authorization code retrieval
 */
export class GoogleAuthHelper {
  private config: GoogleOAuthConfig | null = null;

  /**
   * Initialize Google OAuth with configuration
   */
  initialize(config: GoogleOAuthConfig): void {
    this.config = {
      ...config,
      scope: config.scope || 'openid email profile'
    };
  }

  /**
   * Check if Google OAuth is initialized
   */
  isInitialized(): boolean {
    return this.config !== null;
  }

  /**
   * Generate Google OAuth consent URL
   */
  getAuthorizationUrl(state?: string): string {
    if (!this.isInitialized() || !this.config) {
      throw new Error('Google OAuth not initialized. Call initialize() first.');
    }

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope!,
      access_type: 'offline',
      prompt: 'select_account'
    });

    if (state) {
      params.append('state', state);
    }

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  /**
   * Redirect user to Google OAuth consent screen
   */
  redirectToGoogle(state?: string): void {
    const url = this.getAuthorizationUrl(state);
    window.location.href = url;
  }

  /**
   * Open Google OAuth in popup window
   * Returns authorization code when popup completes
   */
  async signInWithPopup(state?: string): Promise<GoogleAuthResult> {
    if (!this.isInitialized()) {
      throw new Error('Google OAuth not initialized. Call initialize() first.');
    }

    return new Promise((resolve, reject) => {
      const url = this.getAuthorizationUrl(state);
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        url,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Poll for popup closure or message
      const pollTimer = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(pollTimer);
            reject(new Error('OAuth popup was closed'));
          }
        } catch (error) {
          // Cross-origin error is expected
        }
      }, 500);

      // Listen for message from popup (after redirect)
      const messageHandler = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          popup.close();
          resolve({
            code: event.data.code,
            state: event.data.state
          });
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          popup.close();
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };

      window.addEventListener('message', messageHandler);
    });
  }

  /**
   * Parse authorization code from URL (after redirect)
   * Call this on your OAuth callback page
   */
  parseCallbackUrl(url: string = window.location.href): GoogleAuthResult | null {
    try {
      const urlObj = new URL(url);
      const code = urlObj.searchParams.get('code');
      const state = urlObj.searchParams.get('state');
      const error = urlObj.searchParams.get('error');

      if (error) {
        throw new Error(`OAuth error: ${error}`);
      }

      if (!code) {
        return null;
      }

      return {
        code,
        state: state || undefined
      };
    } catch (error) {
      logger.child('AUTH').error('Failed to parse OAuth callback URL:', error);
      return null;
    }
  }

  /**
   * Send OAuth result to parent window (for popup flow)
   * Call this on your OAuth callback page when using popup
   */
  sendResultToParent(result: GoogleAuthResult | { error: string }): void {
    if (window.opener) {
      if ('code' in result) {
        window.opener.postMessage({
          type: 'GOOGLE_OAUTH_SUCCESS',
          code: result.code,
          state: result.state
        }, window.location.origin);
      } else {
        window.opener.postMessage({
          type: 'GOOGLE_OAUTH_ERROR',
          error: result.error
        }, window.location.origin);
      }
    }
  }

  /**
   * Clean up configuration
   */
  destroy(): void {
    this.config = null;
  }
}

// Export singleton instance
export const googleAuthHelper = new GoogleAuthHelper();
export default googleAuthHelper;
